import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { DatePicker } from '@/components/common/DatePicker'
import { DateRangePicker, type DateRange } from '@/components/common/DatePicker/DateRangePicker'
import { toast } from '@/components/common/Toast'
import { Button, Input, Label, Textarea } from '@/components/ui'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { useInitialLocationFilter, useInitialRequiredLocation } from '@/hooks/useInitialLocationFilter'
import { useLocale, useTranslations } from '@/hooks/useTranslations'
import dayjs from '@/lib/dayjs'
import { useCreateExpense, useDeleteExpense, useExpenseList } from '@/modules/expense/services/expense'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/utils/number'

export const Route = createFileRoute('/(dashboard)/admin/expense/')({
  component: ExpensePage,
})

const BKK = 'Asia/Bangkok'

function ymdToDate(ymd: string): Date {
  return dayjs.tz(ymd, 'YYYY-MM-DD', BKK).toDate()
}

function dateToYmd(d: Date): string {
  return dayjs(d).tz(BKK).format('YYYY-MM-DD')
}

/** `YYYY-MM-DD` → local calendar date at noon (stable for API + DatePicker). */
function parseExpenseDateString(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return undefined
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return undefined
  return new Date(y, mo - 1, d, 12, 0, 0, 0)
}

function toExpenseDateString(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function CardShell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/60 bg-card shadow-sm shadow-black/5',
        className,
      )}
    >
      {children}
    </div>
  )
}

const CATEGORY_KEYS = ['cat_general', 'cat_shipping', 'cat_office', 'cat_labor', 'cat_other'] as const
type CategoryKey = (typeof CATEGORY_KEYS)[number]

/** Legacy rows stored translated labels; new rows store canonical keys. */
const LEGACY_CATEGORY_TO_KEY: Record<string, CategoryKey> = {
  ทั่วไป: 'cat_general',
  ค่าขนส่ง: 'cat_shipping',
  ค่าวัสดุสำนักงาน: 'cat_office',
  'ค่าแรง / ช่าง': 'cat_labor',
  อื่นๆ: 'cat_other',
  General: 'cat_general',
  Shipping: 'cat_shipping',
  'Office supplies': 'cat_office',
  'Labor / technician': 'cat_labor',
  Other: 'cat_other',
}

function resolveCategoryKey(stored: string): CategoryKey | null {
  const s = stored.trim()
  if ((CATEGORY_KEYS as readonly string[]).includes(s)) return s as CategoryKey
  return LEGACY_CATEGORY_TO_KEY[s] ?? null
}

function branchLabelKey(loc: BillLocationValue): `location_${BillLocationValue}` {
  return `location_${loc}`
}

function ExpensePage() {
  const t = useTranslations('expenses')
  const tButtons = useTranslations('buttons')
  const locale = useLocale()
  const [page, setPage] = useState(1)
  const [locationFilter, setLocationFilter] = useInitialLocationFilter()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [addModalOpen, setAddModalOpen] = useState(false)
  const listParams = useMemo(
    () => ({
      page,
      limit: 20,
      ...(locationFilter ? { location: locationFilter } : {}),
      ...(dateFrom ? { start_date: dateFrom } : {}),
      ...(dateTo ? { end_date: dateTo } : {}),
    }),
    [page, locationFilter, dateFrom, dateTo],
  )
  const { data, isLoading, refetch } = useExpenseList(listParams)
  const $create = useCreateExpense()
  const $delete = useDeleteExpense()

  useEffect(() => {
    setPage(1)
  }, [locationFilter, dateFrom, dateTo])

  const dateRangeValue = useMemo((): DateRange | undefined => {
    if (!dateFrom && !dateTo) return undefined
    return {
      from: dateFrom ? ymdToDate(dateFrom) : undefined,
      to: dateTo ? ymdToDate(dateTo) : undefined,
    }
  }, [dateFrom, dateTo])

  const onDateRangeChange = (r: DateRange | undefined) => {
    if (!r?.from && !r?.to) {
      setDateFrom('')
      setDateTo('')
      return
    }
    setDateFrom(r.from ? dateToYmd(r.from) : '')
    setDateTo(r.to ? dateToYmd(r.to) : '')
  }

  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [categoryKey, setCategoryKey] = useState<(typeof CATEGORY_KEYS)[number]>('cat_general')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [location, setLocation] = useInitialRequiredLocation()

  const expenseDateValue = useMemo(() => parseExpenseDateString(expenseDate), [expenseDate])

  const formatTableDate = (iso: string) => {
    const d = parseExpenseDateString(iso)
    if (!d) return iso
    return d.toLocaleDateString(locale === 'th' ? 'th-TH' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const resetForm = () => {
    setExpenseDate(new Date().toISOString().slice(0, 10))
    setCategoryKey('cat_general')
    setAmount('')
    setNote('')
  }

  const onAddModalOpenChange = (open: boolean) => {
    setAddModalOpen(open)
    if (!open) resetForm()
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = parseFloat(amount)
    if (!n || n <= 0) {
      toast.toastFailed(t('need_amount'))
      return
    }
    try {
      await $create.mutateAsync({
        expense_date: expenseDate,
        category: categoryKey,
        amount: n,
        note: note.trim() || undefined,
        location,
      })
      toast.toastSuccess(t('saved'))
      resetForm()
      setAddModalOpen(false)
      refetch()
    } catch (err: any) {
      toast.toastFailed(err?.message || t('save_fail'))
    }
  }

  const rows = data?.data ?? []
  const totalPages = data?.meta?.totalPages ?? 1
  const currentPage = data?.meta?.currentPage ?? page
  const amountTotal = data?.meta?.amount_total ?? 0
  const itemTotal = data?.meta?.total ?? 0

  const summaryDateLabel = useMemo(() => {
    if (dateFrom && dateTo) {
      return t('date_range_summary', {
        from: formatTableDate(dateFrom),
        to: formatTableDate(dateTo),
      })
    }
    if (dateFrom) return formatTableDate(dateFrom)
    if (dateTo) return formatTableDate(dateTo)
    return t('all_dates')
  }, [dateFrom, dateTo, locale, t])

  const locationSummaryLabel = locationFilter
    ? t(branchLabelKey(locationFilter as BillLocationValue))
    : t('all_locations')

  return (
    <div className="w-full min-w-0 max-w-none space-y-6 pb-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 md:text-3xl">
            {t('page_title')}
          </h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground md:text-base">{t('hero_subtitle')}</p>
        </div>
        <Button
          type="button"
          className="h-11 shrink-0 rounded-lg bg-slate-800 px-5 text-white hover:bg-slate-900 dark:bg-slate-800 dark:hover:bg-slate-900"
          onClick={() => setAddModalOpen(true)}
        >
          <Plus className="mr-2 size-4" />
          {t('add_section')}
        </Button>
      </div>

      <CardShell className="p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label className="text-xs text-muted-foreground">{t('location_filter')}</Label>
            <Select
              value={locationFilter || '__all__'}
              onValueChange={(v) =>
                setLocationFilter(v === '__all__' ? '' : (v as BillLocationValue))
              }
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder={t('all_locations')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('all_locations')}</SelectItem>
                {BILL_LOCATION_VALUES.map((loc) => (
                  <SelectItem key={loc} value={loc}>
                    {t(branchLabelKey(loc))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-xs text-muted-foreground">{t('date_range_filter')}</Label>
            <DateRangePicker
              className="h-10 w-full min-w-0 justify-between rounded-lg border-slate-200"
              value={dateRangeValue}
              onChange={onDateRangeChange}
            />
          </div>
        </div>
      </CardShell>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-5 shadow-sm">
          <p className="text-xs font-medium text-muted-foreground">{t('total_amount')}</p>
          {isLoading ? (
            <div className="mt-2 h-8 w-32 animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-1 text-2xl font-bold tabular-nums text-t-warning">{formatPrice(amountTotal)}</p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">{locationSummaryLabel}</p>
        </Card>
        <Card className="rounded-2xl border-border/60 p-5 shadow-sm">
          <p className="text-xs font-medium text-muted-foreground">{t('summary_period')}</p>
          {isLoading ? (
            <div className="mt-2 h-8 w-full max-w-[200px] animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-1 text-lg font-semibold leading-snug text-slate-900 dark:text-slate-100">
              {summaryDateLabel}
            </p>
          )}
        </Card>
        <Card className="rounded-2xl border-border/60 p-5 shadow-sm">
          <p className="text-xs font-medium text-muted-foreground">{t('entry_count')}</p>
          {isLoading ? (
            <div className="mt-2 h-8 w-16 animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
              {itemTotal.toLocaleString(locale === 'th' ? 'th-TH' : 'en-US')}
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">{t('total_items', { count: itemTotal })}</p>
        </Card>
      </div>

      <CardShell>
        <div className="border-b border-border/60 px-6 py-4">
          <h2 className="text-lg font-semibold tracking-tight">{t('latest_section')}</h2>
        </div>
        {isLoading ? (
          <div className="p-8 text-sm text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-muted-foreground">
                  <th className="px-6 py-3 font-medium">{t('date')}</th>
                  <th className="px-6 py-3 font-medium">{t('location')}</th>
                  <th className="px-6 py-3 font-medium">{t('category')}</th>
                  <th className="px-6 py-3 font-medium text-right">{t('amount')}</th>
                  <th className="px-6 py-3 font-medium">{t('note')}</th>
                  <th className="w-24 px-6 py-3 font-medium text-muted-foreground">{t('col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/40 last:border-0">
                    <td className="whitespace-nowrap px-6 py-3">{formatTableDate(row.expense_date)}</td>
                    <td className="px-6 py-3">
                      {row.location && BILL_LOCATION_VALUES.includes(row.location as BillLocationValue)
                        ? t(branchLabelKey(row.location as BillLocationValue))
                        : t('unknown_branch')}
                    </td>
                    <td className="px-6 py-3">
                      {(() => {
                        const key = resolveCategoryKey(row.category)
                        return key ? t(key) : row.category
                      })()}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-right font-medium tabular-nums">
                      {formatPrice(row.amount)}
                    </td>
                    <td className="max-w-[280px] px-6 py-3 text-muted-foreground">
                      <span className="line-clamp-2">{row.note?.trim() ? row.note : t('no_note')}</span>
                    </td>
                    <td className="px-6 py-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={async () => {
                          try {
                            await $delete.mutateAsync({ id: row.id })
                            toast.toastSuccess(t('deleted'))
                            refetch()
                          } catch (e: any) {
                            toast.toastFailed(e?.message || t('delete_fail'))
                          }
                        }}
                      >
                        {t('delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 ? (
              <p className="border-t border-border/40 px-6 py-6 text-center text-sm text-muted-foreground">
                {t('empty_list')}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-6 py-4 text-sm">
          <Button type="button" variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)}>
            {tButtons('previous')}
          </Button>
          <span className="text-muted-foreground">
            {t('page_indicator', { current: currentPage, total: totalPages })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {tButtons('next')}
          </Button>
        </div>
      </CardShell>

      <Dialog open={addModalOpen} onOpenChange={onAddModalOpenChange}>
        <DialogContent className="max-w-lg gap-0 p-0 sm:max-w-xl">
          <DialogHeader className="border-b border-border/60 px-6 py-4">
            <DialogTitle>{t('add_section')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-5 px-6 py-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="exp-date" className="text-sm">
                  {t('date')}
                </Label>
                <DatePicker
                  id="exp-date"
                  value={expenseDateValue}
                  onChange={(d) => {
                    if (d) setExpenseDate(toExpenseDateString(d))
                  }}
                  className="mt-1.5 h-10 w-full justify-between"
                  align="start"
                />
              </div>
              <div>
                <Label className="text-sm">{t('location')}</Label>
                <Select value={location} onValueChange={(v) => setLocation(v as BillLocationValue)}>
                  <SelectTrigger className="mt-1.5 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILL_LOCATION_VALUES.map((loc) => (
                      <SelectItem key={loc} value={loc}>
                        {t(branchLabelKey(loc))}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">{t('category')}</Label>
                <Select
                  value={categoryKey}
                  onValueChange={(v) => setCategoryKey(v as (typeof CATEGORY_KEYS)[number])}
                >
                  <SelectTrigger className="mt-1.5 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_KEYS.map((key) => (
                      <SelectItem key={key} value={key}>
                        {t(key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="exp-amt" className="text-sm">
                  {t('amount')}
                </Label>
                <Input
                  id="exp-amt"
                  className="mt-1.5 h-10"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t('amount_placeholder')}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="exp-note" className="text-sm">
                  {t('note')}
                </Label>
                <Textarea
                  id="exp-note"
                  className="mt-1.5 min-h-[100px] resize-y"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter className="gap-2 border-t border-border/60 px-0 pb-0 pt-4 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onAddModalOpenChange(false)}>
                {tButtons('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={$create.isLoading}
                className="bg-slate-800 text-white hover:bg-slate-900 dark:bg-slate-800 dark:hover:bg-slate-900"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
